// Carga de datos de Estadísticas 2.
//
// La pantalla actual hace `getDocs(statsRef)` sin filtro: descarga TODOS los
// documentos de la colección —hasta 395 diarios, más los mensuales, varios con
// JSON de categorías y anulados adentro— y recién ahí se queda con los últimos
// 90 en memoria. El resto se paga en tiempo y en cuota de lectura, y se tira.
//
// Acá se pide solamente lo que se va a mostrar. Los diarios se filtran por el
// campo `date` (índice de un solo campo, automático en Firestore, no hay que
// crear nada) y los mensuales van en su propia consulta, porque no tienen ese
// campo y quedarían afuera del filtro igual.

import { useEffect, useState, useCallback, useMemo } from "react";
import { doc, getDocs, onSnapshot, query, where, orderBy, limit } from "firebase/firestore";
import { getVersionedCollection } from "../../utils/firestorePaths";

const hoyISO = () => {
  const d = new Date();
  const p = (x) => String(x).padStart(2, "0");
  // Local, NO toISOString(): eso es UTC y a partir de las 21:00 en Argentina
  // devuelve el día siguiente, así que el "hoy" del dashboard se adelantaría.
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const desdeHace = (dias) => {
  const d = new Date();
  d.setDate(d.getDate() - dias);
  const p = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/**
 * @param {string} companyId
 * @param {number} syncVersion
 * @param {number} dias  cuántos días de historia traer.
 *
 * 395 y no 120: es el TTL del documento diario en Firestore (`addTTL(payload,
 * 395)` en functions/Monitor/stats.js), o sea todo lo que existe. Pedir menos
 * no ahorraba nada —la consulta es por rango sobre un índice, no una descarga
 * completa— y en cambio recortaba en silencio la comparación contra el mismo
 * mes del año pasado, que es justo la que el dueño quiere para decidir precios.
 */
export default function useStatsData(companyId, syncVersion, dias = 395) {
  const [diarios, setDiarios] = useState([]);
  const [mensuales, setMensuales] = useState([]);
  const [vivo, setVivo] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  // ── histórico ────────────────────────────────────────────────────────
  const cargar = useCallback(async () => {
    if (!companyId) return;
    setCargando(true);
    setError(null);
    try {
      const ref = getVersionedCollection(companyId, "stats", syncVersion);

      const [snapDiarios, snapMensuales] = await Promise.all([
        getDocs(query(ref, where("date", ">=", desdeHace(dias)), orderBy("date", "desc"))),
        // Los mensuales no tienen `date`, así que van aparte. Sin límite: son
        // uno por mes y el dashboard de tendencia larga los quiere todos.
        getDocs(query(ref, orderBy("month", "desc"), limit(60))),
      ]);

      setDiarios(snapDiarios.docs.map((d) => ({ id: d.id, ...d.data() })));
      setMensuales(snapMensuales.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error("Stats2: error cargando estadísticas", e);
      // Un filtro que falla y una cuenta sin datos terminan los dos en cero
      // filas. Sin este mensaje, el dueño lee "no hay datos" cuando en realidad
      // la pantalla no pudo preguntar.
      setError(
        /index/i.test(e?.message || "") || e?.code === "failed-precondition"
          ? "Esta vista necesita un índice que todavía no está creado en la base. Avísale a soporte."
          : "No se pudieron cargar las estadísticas. Revisa la conexión y vuelve a intentar."
      );
    } finally {
      setCargando(false);
    }
  }, [companyId, syncVersion, dias]);

  useEffect(() => { cargar(); }, [cargar]);

  // ── el día de hoy, en vivo ───────────────────────────────────────────
  //
  // Se escucha aparte del histórico: es el único documento que cambia mientras
  // el dueño está mirando, y volver a bajar 120 días para refrescarlo sería
  // absurdo.
  useEffect(() => {
    if (!companyId) return;
    const ref = getVersionedCollection(companyId, "stats", syncVersion);
    const hoy = hoyISO();
    const unsub = onSnapshot(
      doc(ref, `daily_${hoy}`),
      (snap) => {
        if (!snap.exists()) return;
        const actualizado = { id: snap.id, ...snap.data() };
        setDiarios((prev) => {
          const i = prev.findIndex((d) => d.id === actualizado.id);
          if (i >= 0) {
            const copia = [...prev];
            copia[i] = actualizado;
            return copia;
          }
          return [actualizado, ...prev];
        });
      },
      (e) => console.error("Stats2: listener del día de hoy", e)
    );
    return () => unsub();
  }, [companyId, syncVersion]);

  // ── el pulso: ocupación de este momento ──────────────────────────────
  useEffect(() => {
    if (!companyId) return;
    const ref = getVersionedCollection(companyId, "stats", syncVersion);
    const unsub = onSnapshot(
      doc(ref, "live"),
      (snap) => { if (snap.exists()) setVivo(snap.data()); },
      (e) => console.error("Stats2: listener de live", e)
    );
    return () => unsub();
  }, [companyId, syncVersion]);

  // ── derivados que casi todos los bloques necesitan ───────────────────
  const derivados = useMemo(() => {
    const orden = [...diarios].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    const hoy = hoyISO();
    const cerrados = orden.filter((d) => d.date && d.date < hoy);

    return {
      // Ascendente y sin el día en curso: casi todos los cálculos —promedios,
      // tendencias, comparaciones— se distorsionan si les entra un día a medias.
      cerrados,
      hoy: orden.find((d) => d.date === hoy) || null,
      // Desde cuándo hay datos de verdad. Un cliente de 3 días no puede ver una
      // pantalla que le promete 90.
      diasConDatos: cerrados.length,
      primerDia: cerrados.length ? cerrados[0].date : null,
      // ¿Este cliente manda la curva de ocupación? Los que no actualizaron
      // todavía no la traen, y hay bloques que no tienen sentido sin ella.
      tieneOcupacion: cerrados.some((d) => d.occupancy && Array.isArray(d.occupancy.hourly)),
      tienePermanencia: cerrados.some((d) => d.stay && d.stay.avg_hours > 0),
      tieneAbonados: cerrados.some((d) => (d.monthly?.amount || 0) > 0),
      tieneConvenios: cerrados.some((d) => (d.deals?.amount || 0) > 0),
    };
  }, [diarios]);

  return { diarios, mensuales, vivo, cargando, error, recargar: cargar, ...derivados };
}
